/* eslint-disable no-underscore-dangle */
import Vue from 'vue';
import nonreactive from '@/lib/nonreactive';
import handleErrors from '@/lib/handleErrors';
import buildApiQuery from '@/lib/buildApiQuery';
import isEqual from 'lodash/isEqual';

const allowedDistances = {
  ELEC: {
    allowedDistance: 50,
  },
  HY: {
    allowedDistance: 100,
  },
  LPG: {
    allowedDistance: 150,
  },
  CNG: {
    allowedDistance: 150,
  },
  LNG: {
    allowedDistance: 200,
  },
};

const moduleState = {
  currentCorridorApiQuery: {},
  corridorError: null,
  corridorRouteStart: {},
  corridorRouteEnd: {},
  corridorDistance: null,
  corridorRouteIndex: 0,
  corridorRouteAllInfo: [],
  corridorDistanceToHighway: {},
  corridorStationsCheck: true,
  showCorridorShapefile: false,
  corridorLineGeojson: {
    type: 'FeatureCollection',
    features: [],
  },
  corridorDirections: [],
  highwayDistances: null,
  fuelCorridorGeojson: {
    type: 'FeatureCollection',
    features: [],
  },
  corridorState: {
    type: 'FeatureCollection',
    features: [],
  },
  routeType: 'highway',
  corridorColors: {
    ELEC: {
      ready: '#31a354',
      pending: '#a1d99b',
    },
    HY: {
      ready: '#08519c',
      pending: '#4292c6',
    },
    CNG: {
      ready: '#08519c',
      pending: '#4292c6',
    },
    LNG: {
      ready: '#31a354',
      pending: '#a1d99b',
    },
    LPG: {
      ready: '#807dba',
      pending: '#bcbddc',
    },
  },
};

const moduleGetters = {
  getCorridorRouteStart: (state) => state.corridorRouteStart,
  getCorridorRouteEnd: (state) => state.corridorRouteEnd,
  getCorridorError: (state) => state.corridorError,
  getCorridorState: (state) => state.corridorState,
  corridorStationsLineGeojson: (state) => state.corridorLineGeojson,
  getCorridorDistance: (state) => state.corridorDistance,
  getCorridorDistanceToHighway: (state) => state.corridorDistanceToHighway,
  getCorridorDirections: (state) => state.corridorDirections,
  getCorridorRouteAllInfo: (state) => state.corridorRouteAllInfo,
  getCorridorRouteIndex: (state) => state.corridorRouteIndex,
  getCorridorStationsCheck: (state) => state.corridorStationsCheck,
  getCorridorColors: (state) => state.corridorColors,
  getShowCorridorShapefile: (state) => state.showCorridorShapefile,
  getCorridorDistanceAllowed: (state, getters) => {
    const fuel = getters.getFilterValueWithDefault('fuel')[0];
    if (fuel && allowedDistances[fuel]) {
      return allowedDistances[fuel].allowedDistance;
    }
    return null;
  },
  getRouteType: (state) => state.routeType,
  getRouteTypeQuery: (state, getters) => {
    const routeType = getters.getRouteType;
    return routeType === 'highway' ? 'highway' : '';
  },
  isCorridorDistanceOverAllowed: (state, getters) => {
    const newDistance = getters.getCorridorDistance || null;
    const distanceAllowed = getters.getCorridorDistanceAllowed || null;
    if (newDistance > distanceAllowed) {
      return true;
    }
    return false;
  },
};

const mutations = {
  setCorridorRouteStart(state, station) {
    state.corridorRouteStart = station;
  },

  setCorridorRouteEnd(state, station) {
    state.corridorRouteEnd = station;
  },

  setClearCorridor(state) {
    const lineGeojson = {
      type: 'FeatureCollection',
      features: [],
    };
    state.corridorLineGeojson = lineGeojson;
  },

  setCorridorError(state, status) {
    state.corridorError = status;
  },

  setCorridorStationsCheck(state, status) {
    state.corridorStationsCheck = status;
  },

  setShowCorridorShapefile(state, status) {
    state.showCorridorShapefile = status;
  },

  setCorridorIndex(state, routeId) {
    state.corridorRouteIndex = routeId;
    const routeArray = state.corridorRouteAllInfo;
    state.corridorDistance = routeArray[routeId]._locator.distance;
    state.corridorDistanceToHighway = routeArray[routeId]._locator.highway_distances || null;
    state.corridorDirections = routeArray[routeId]._locator.directions.maneuvers;
  },

  resetCorridorRouteIndex(state, routeId) {
    state.corridorRouteIndex = routeId;
  },

  setCorridorState(state, stateInfo) {
    const stateGeojson = {
      type: 'FeatureCollection',
      features:
      [{
        type: 'Feature',
        properties: {
          name: stateInfo.name,
          abbreviation: stateInfo.abbreviation,
        },
        geometry: stateInfo.geometry,
      }],
    };

    state.corridorState = nonreactive.frozen(stateGeojson);
  },

  setRouteType(state, routePreference) {
    state.routeType = routePreference;
  },

  saveCorridorStations(state, payload) {
    // eslint-disable-next-line no-underscore-dangle

    const multipleRoutesGeo = {
      type: 'FeatureCollection',
      features: [],
    };

    // eslint-disable-next-line no-empty
    if (payload && payload.data && payload.data.routes[0]._locator.directions) {
      payload.data.routes.forEach((route, index) => {
        multipleRoutesGeo.features.push({
          type: 'Feature',
          id: index,
          properties: {
            layerIndex: index,
          },
          geometry: {
            type: 'LineString',
            coordinates: route._locator.route.coordinates,
          },
        });
      });
      state.corridorRouteAllInfo = payload.data.routes;
      state.corridorDistance = payload.data.routes[0]._locator.distance;
      state.corridorDistanceToHighway = payload.data.routes[0]._locator.highway_distances || null;
      state.corridorDirections = payload.data.routes[0]._locator.directions.maneuvers;
    } else {
      state.corridorRouteAllInfo = [];
      state.corridorDistance = null;
      state.corridorDistanceToHighway = {};
      state.corridorDirections = [];
    }

    state.corridorLineGeojson = nonreactive.frozen(multipleRoutesGeo);
    if (payload) {
      state.currentCorridorApiQuery = payload.apiQuery;
    }
  },

};

const actions = {
  async fetchCorridorStations({ commit, state, getters }, query) {
    try {
      const apiQuery = {
        ...buildApiQuery(getters, query),
        _from: query.start,
        _to: query.end,
        _prefers: getters.getRouteTypeQuery,
      };

      if (isEqual(apiQuery, state.currentCorridorApiQuery)) {
        return;
      }

      commit('setLoadingCorridorStations', true);
      commit('saveRouteCurrentApiQuery', apiQuery);
      // If the start or end location is empty, then return an empty result without
      // actually performing an ajax query.
      // eslint-disable-next-line no-underscore-dangle
      if (apiQuery._from === undefined || apiQuery._from === null || apiQuery._from === '' || apiQuery._to === undefined || apiQuery._to === null || apiQuery._to === '') {
        commit('saveCorridorStations', {
          apiQuery,
        });
        commit('setLoadingCorridorStations', false);
        commit('setCorridorError', handleErrors());
        return;
      }

      const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL_ROOT}/v1/locator/directions.json`, {
        params: apiQuery,
      });

      commit('saveCorridorStations', {
        apiQuery,
        data: response.data,
      });
      commit('resetCorridorRouteIndex', 0);
      commit('setCorridorError', null);
    } catch (e) {
      commit('saveCorridorStations');
      commit('setCorridorError', handleErrors(e));
    } finally {
      commit('setLoadingCorridorStations', false);
    }
  },
};

export default {
  state: moduleState,
  getters: moduleGetters,
  mutations,
  actions,
};
